/**
 * VLE API Service
 *
 * Domain-specific service for VLE (Village Level Entrepreneur) operations.
 * Handles consent list, creation, and related VLE-specific API calls.
 *
 * @author Claude Code
 * @date February 2026
 * @doc Reference: Doc 32 - Phase 2 Implementation
 */

import { httpClient } from './http-client.service';
import type {
  VleConsentListParams,
  VleConsentListResponse,
  VleConsentCreatePayload,
  VleConsentCreateResponse,
  VleConsentSyncResponse,
  PaymentInitiateResponse,
  PaymentConfigResponse,
  PaymentStatusResponse,
  PaymentReversalResponse,
  ShareStatementResponse,
} from '../components/pages/vle-consent/types';

class VleAPIService {
  /**
   * Fetch VLE consent list with pagination and filters.
   * This endpoint ALWAYS returns only consents created by the logged-in VLE.
   *
   * @param params - Pagination and filter parameters
   * @returns Promise with consent list response
   */
  async getConsentList(params: VleConsentListParams): Promise<VleConsentListResponse> {
    const response = await httpClient.post<VleConsentListResponse>('csc/consent/list', params);
    return response.data;
  }

  /**
   * Create a new consent request.
   * Uses the V3 endpoint which supports config.fiDataRange for custom date ranges.
   *
   * @param payload - Consent creation payload with VLE-specific fields
   * @returns Promise with consent creation response
   */
  async createConsentRequest(payload: VleConsentCreatePayload): Promise<VleConsentCreateResponse> {
    // Ensure request_type is V3 for fiDataRange support
    const requestPayload = {
      ...payload,
      request_type: 'V3',
    };
    const response = await httpClient.post<VleConsentCreateResponse>('v3/requestconsent', requestPayload);
    return response.data;
  }

  /**
   * Fetch available products for the consent form dropdown.
   *
   * @returns Promise with products list
   */
  async getProducts(): Promise<any> {
    const response = await httpClient.post('productslist', {});
    return response.data;
  }

  /**
   * Fetch available FIPs (Financial Information Providers) for bank selection.
   *
   * @returns Promise with FIP list
   */
  async getFipList(): Promise<any> {
    const response = await httpClient.get('list/fips');
    return response.data;
  }

  /**
   * Initiate payment for a consent.
   * Returns auto-submitting HTML form that POSTs to CSC Bridge PG.
   *
   * @param consentId - The consent_id to pay for
   * @param mobileNo - Mobile number for display on payment page
   * @returns Promise with payment initiation response
   */
  async initiatePayment(consentId: string, mobileNo?: string): Promise<PaymentInitiateResponse> {
    const response = await httpClient.post<PaymentInitiateResponse>('vle/payment/initiate', {
      consent_id: consentId,
      mobile_no: mobileNo,
    });
    return response.data;
  }

  /**
   * Get payment configuration (amount, enabled status).
   *
   * @returns Promise with payment config
   */
  async getPaymentConfig(): Promise<PaymentConfigResponse> {
    const response = await httpClient.get<PaymentConfigResponse>('vle/payment/config');
    return response.data;
  }

  /**
   * Check payment status by calling CSC Transaction Status API.
   * Sends consent_id — FinProServer looks up latest merchant_txn and calls CSC.
   *
   * [SOURCE-TRUTH] FinproCSC/files/06-api-transaction-status.md
   *
   * @param consentId - The consent_id to check payment status for
   * @returns Promise with payment status response
   */
  async checkPaymentStatus(consentId: string): Promise<PaymentStatusResponse> {
    const response = await httpClient.post<PaymentStatusResponse>('vle/payment/status', {
      consent_id: consentId,
    });
    return response.data;
  }

  /**
   * OneApp CSC/VLE data sync — called on the iframe SUCCESS event.
   * The backend (FinProServer via AdminServer) does the fetchAAData + store + flags;
   * this just triggers it. Returns { status: 'ACTIVE' | 'NO_DATA', has_data }.
   *
   * @param consentHandle - the consent handle returned by the create (getWebrdUrl) response
   */
  async syncConsent(consentHandle: string): Promise<VleConsentSyncResponse> {
    const response = await httpClient.post<VleConsentSyncResponse>('csc/consent/sync', {
      consent_handle: consentHandle,
    });
    return response.data;
  }

  /**
   * Reverse a UNKNOWN (000) payment by calling CSC Transaction Reversal API.
   * AIDEV-NOTE: CSC Phase 2 — Transaction Reversal (API 2 of 5)
   *
   * [SOURCE-TRUTH] FinproCSC/files/07-api-reversal-refund.md
   *
   * @param consentId - The consent_id to reverse payment for
   * @returns Promise with reversal response
   */
  async reversePayment(consentId: string): Promise<PaymentReversalResponse> {
    const response = await httpClient.post<PaymentReversalResponse>('vle/payment/reverse', {
      consent_id: consentId,
    });
    return response.data;
  }

  /**
   * Share statement via SMS (Phase 5).
   * Generates PDF, uploads to S3, creates short URL, and sends SMS to customer.
   * AIDEV-NOTE: Uses Mock SMS provider for testing - check shared_url_records table for URL
   *
   * @param consentId - The consent_id to share statement for
   * @param mobile - Mobile number to send SMS to
   * @returns Promise with share statement response
   */
  async shareStatement(consentId: string, mobile: string): Promise<ShareStatementResponse> {
    const response = await httpClient.post<ShareStatementResponse>('pdfstatement/share', {
      consent_id: consentId,
      mobile: mobile,
    });
    return response.data;
  }

  /**
   * Log the VLE out. Best-effort server-side session teardown — routed through
   * httpClient so the base URL and token rotation are handled consistently.
   */
  async logout(): Promise<void> {
    try {
      await httpClient.get('csc/auth/logout');
    } catch {
      // Ignore — the caller clears the local session regardless.
    }
  }
}

export const vleAPI = new VleAPIService();
